import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, ShieldCheck, Lightbulb, Zap, Truck, Award, Settings2, ArrowRight, Star, CheckCircle2 } from 'lucide-react';
import { BANNERS } from '../data/assets';
import { getProducts, getCategories } from '../lib/api';
import ProductCard from '../components/ProductCard';
import GetInTouch from '../components/GetInTouch';

const FEATURES = [
  { icon: Leaf, title: 'Energy Efficient', sub: 'Save up to 86%' },
  { icon: ShieldCheck, title: 'Long Lasting', sub: '30,000+ Hours' },
  { icon: Lightbulb, title: 'High Brightness', sub: 'Better Illumination' },
  { icon: ShieldCheck, title: 'Safe & Reliable', sub: 'Built to Last' },
  { icon: Settings2, title: 'Advanced Technology', sub: 'Smart LED' },
  { icon: Truck, title: 'Nationwide Delivery', sub: 'Fast & Free' },
];

const Home = () => {
  const [slide, setSlide] = useState(0);
  const [featured, setFeatured] = useState([]);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    getProducts({ featured: true }).then(setFeatured).catch(() => {});
    getProducts().then(setProducts).catch(() => {});
    getCategories().then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % BANNERS.length), 5000);
    return () => clearInterval(t);
  }, []);

  const byCategory = (name) => products.filter(p => p.category === name);

  return (
    <div>
      {/* Hero carousel */}
      <section className="relative bg-[#0d5228]">
        <div className="max-w-[1600px] mx-auto relative overflow-hidden" style={{ aspectRatio: '1600 / 640' }}>
          {BANNERS.map((b, i) => (
            <img key={i} src={b} alt={`banner ${i + 1}`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === slide ? 'opacity-100' : 'opacity-0'}`} />
          ))}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {BANNERS.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)}
                className={`h-2.5 rounded-full transition-all ${i === slide ? 'w-8 bg-[#8bc34a]' : 'w-2.5 bg-white/60'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="bg-[#0b3d1e]">
        <div className="max-w-7xl mx-auto px-4 py-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {FEATURES.map((f, i) => (
            <div key={i} className="flex items-center gap-3 text-white">
              <div className="w-11 h-11 rounded-full border-2 border-[#37b34a] flex items-center justify-center shrink-0">
                <f.icon size={20} className="text-[#8bc34a]" />
              </div>
              <div>
                <div className="text-sm font-semibold leading-tight">{f.title}</div>
                <div className="text-[11px] text-gray-300">{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="section-soft">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center mb-10">
            <span className="text-[#37b34a] font-semibold uppercase tracking-wider text-sm">Explore</span>
            <h2 className="font-head text-3xl md:text-4xl font-extrabold text-[#14181b] mt-2">Shop by Category</h2>
            <div className="w-20 h-1 bg-[#37b34a] mx-auto mt-4 rounded-full" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {cats.map(c => (
              <Link key={c.slug} to={`/shop?category=${encodeURIComponent(c.name)}`}
                className="group bg-white rounded-xl border border-gray-100 p-5 text-center card-hover">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#eaf6ec] flex items-center justify-center mb-3 group-hover:bg-[#37b34a] transition-colors">
                  <Lightbulb size={24} className="text-[#0f7a37] group-hover:text-white transition-colors" />
                </div>
                <div className="font-head font-semibold text-sm text-[#14181b] leading-tight">{c.name.replace(' Series', '')}</div>
                <div className="text-xs text-gray-400 mt-1">{c.count} items</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-[#37b34a] font-semibold uppercase tracking-wider text-sm flex items-center gap-1.5"><Star size={15} fill="#37b34a" /> Best Sellers</span>
              <h2 className="font-head text-3xl md:text-4xl font-extrabold text-[#14181b] mt-2">Featured Products</h2>
            </div>
            <Link to="/shop" className="nl-btn text-[#0f7a37] font-semibold hidden sm:flex items-center gap-1 hover:gap-2">View All <ArrowRight size={18} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {featured.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Promo banner */}
      <section className="max-w-7xl mx-auto px-4">
        <div className="hero-gradient rounded-3xl p-10 md:p-14 text-white relative overflow-hidden">
          <div className="relative z-10 max-w-xl">
            <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-medium mb-4"><Award size={16} /> 6+ Years of Trust</div>
            <h2 className="font-head text-3xl md:text-4xl font-extrabold leading-tight">Smart Technology for a <span className="text-[#8bc34a]">Brighter Future</span></h2>
            <p className="mt-4 text-gray-100">Premium LED lighting engineered for maximum energy savings, superior brightness and long-lasting performance. Order directly on WhatsApp.</p>
            <Link to="/shop" className="nl-btn bg-[#8bc34a] hover:bg-[#7cb342] text-[#0b3d1e] px-7 py-3 mt-6">Shop Now <ArrowRight size={18} /></Link>
          </div>
          <Zap className="absolute -right-6 -bottom-6 text-white/10" size={240} />
        </div>
      </section>

      {/* Category-wise product sections */}
      {cats.map(c => {
        const list = byCategory(c.name).slice(0, 4);
        if (list.length === 0) return null;
        return (
          <section key={c.slug} className="max-w-7xl mx-auto px-4 py-12">
            <div className="flex items-end justify-between mb-6">
              <div>
                <span className="text-[#37b34a] font-semibold uppercase tracking-wider text-xs">Collection</span>
                <h2 className="font-head text-2xl md:text-3xl font-extrabold text-[#14181b] mt-1">{c.name}</h2>
              </div>
              <Link to={`/shop?category=${encodeURIComponent(c.name)}`} className="nl-btn text-[#0f7a37] font-semibold text-sm flex items-center gap-1 hover:gap-2">View All <ArrowRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {list.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        );
      })}

      {/* About section */}
      <section className="section-soft">
        <div className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img src={BANNERS[0]} alt="National Light" className="rounded-2xl object-cover h-40 w-full col-span-2 border border-gray-100" />
            <div className="bg-[#0f7a37] rounded-2xl p-6 text-white">
              <div className="font-head text-4xl font-extrabold">6+</div>
              <div className="text-sm text-green-50 mt-1">Years of Trusted Quality</div>
            </div>
            <div className="bg-[#eaf6ec] rounded-2xl p-6">
              <div className="font-head text-4xl font-extrabold text-[#0d5228]">50K+</div>
              <div className="text-sm text-gray-600 mt-1">Satisfied Customers</div>
            </div>
          </div>
          <div>
            <span className="text-[#37b34a] font-semibold uppercase tracking-wider text-sm">About National Light</span>
            <h2 className="font-head text-3xl font-extrabold text-[#14181b] mt-2">Lighting Up Pakistan, One Space at a Time</h2>
            <p className="text-gray-600 mt-4 leading-relaxed">National Light is a trusted name in premium LED lighting. We design and manufacture energy-efficient bulbs, panel lights, downlights and flood lights that combine brilliant illumination with exceptional durability and savings.</p>
            <ul className="mt-5 space-y-2.5">
              {['Premium quality with 14-month warranty', 'Up to 86% energy savings', 'Eye-comfort, flicker-free technology', 'Nationwide delivery & support'].map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700"><CheckCircle2 size={18} className="text-[#0f7a37] shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
            <Link to="/about" className="nl-btn bg-[#0f7a37] hover:bg-[#0d5228] text-white px-7 py-3 mt-7">Learn More <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      {/* Get in touch */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-[#37b34a] font-semibold uppercase tracking-wider text-sm">Contact</span>
            <h2 className="font-head text-3xl md:text-4xl font-extrabold text-[#14181b] mt-2">Get In Touch</h2>
            <p className="text-gray-600 mt-4 leading-relaxed">Looking for the perfect lighting solution or planning a bulk order? Send us a message and our team will get back to you promptly. You can also order instantly on WhatsApp.</p>
            <div className="mt-6 space-y-3">
              {FEATURES.slice(0, 3).map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#eaf6ec] flex items-center justify-center"><f.icon size={18} className="text-[#0f7a37]" /></div>
                  <span className="text-sm font-medium text-gray-700">{f.title} — {f.sub}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
            <GetInTouch compact />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
